import http from "@http";
import config from "@config";
import { all, call, put, select, takeLatest, spawn } from "redux-saga/effects";
import { push } from "connected-react-router";
import { getCurrentPath } from "@selectors";
import { HOME_PATH } from "@routes/constants";
import { CheckLogin, GetAccountAbsenceManagementSettings } from "../constants/actions";
import actionCreators from "../actionCreator";
import { isAuthenticated } from "../selectors";
import resetPasswordSaga from "./reset-password";
import impersonateSaga from "./impersonate";
import LocalStorageService from "@services/LocalStorageService";
import sharedActionCreators from "@modules/shared/actionCreator";
import { GetAccountClientServiceSettings } from "@modules/account/constants/actions";
import type { Action } from "redux-action";
import reportingActionCreators from "@modules/reporting/actionCreator";
import { USER_TYPES } from "@modules/account/constants";

export function* onCheckRedirect(data) {
  try {
    const isLoggedIn = yield select(isAuthenticated);
    const currentPath = yield select(getCurrentPath);

    if (LocalStorageService.getImpersonate()) {
      LocalStorageService.clearImpersonate();
      yield put(push(HOME_PATH));
      return;
    }
  } catch (e) {
    console.error("Error", e);
  }
}

export function* onCheckLogin() {
  try {
    const response = yield call(http.get, `${config.endpointBaseUrl}api/v1/profile/me`);

    const permissions = response.data.permissions.reduce(
      (acc, permission) => ({
        ...acc,
        [permission]: true,
      }),
      {},
    );

    const data = {
      ...response.data,
      permissions,
    };

    yield put(
      actionCreators.AuthenticateSuccess({
        user: data.user,
        permissions,
        lastThreeSuccessfulLogins: data.lastThreeSuccessfulLogins,
      }),
    );

    yield put(sharedActionCreators.GetMessagesReleaseVersion());

    const allowFetchDashboardOverview = !![
      USER_TYPES.CLIENT_SUPPLIER,
      USER_TYPES.ADMIN,
      USER_TYPES.SERVICE_MANAGER,
    ].includes(data.user.userType);

    if (allowFetchDashboardOverview) {
      yield put(
        reportingActionCreators.GetLoggedUserDashboardOverview({
          clientId: data.user.clientId,
        }),
      );
    }

    yield onCheckRedirect(response.data);
  } catch (e) {
    yield put(actionCreators.AuthenticateError(e.response));
  }
}

export function* onGetAccountClientServiceSettings({
  payload,
}: Action<{ clientId: number }>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/service-settings`,
    );

    yield put(actionCreators.GetAccountClientServiceSettingsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetAccountClientServiceSettingsError((e || {}).response));
  }
}

export function* onGetAccountAbsenceManagementSettings({
  payload,
}: Action<{ clientId: number }>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `/api/v1/client-admin/client/${payload.clientId}/service/absence-management`,
    );

    yield put(actionCreators.GetAccountAbsenceManagementSettingsSuccess(response.data.settings));
  } catch (e) {
    yield put(actionCreators.GetAccountAbsenceManagementSettingsError((e || {}).response));
  }
}

export function* loginSaga() {
  yield all([
    takeLatest(CheckLogin, onCheckLogin),
    takeLatest(GetAccountClientServiceSettings, onGetAccountClientServiceSettings),
    takeLatest(GetAccountAbsenceManagementSettings, onGetAccountAbsenceManagementSettings),
    spawn(resetPasswordSaga),
    spawn(impersonateSaga),
  ]);
}

export default loginSaga;
